import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, List, ListItem, ListItemIcon, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Popper, Checkbox, useTheme } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
//import './technologyPartnersTab.scss';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ArrowDropDown } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Check, KeyboardArrowRight } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { visuallyHidden } from '@mui/utils';
import { useNavigate } from 'react-router-dom';


interface capabilitiesMap {
  gds_content_integration: string;
  air_shopping: string;
  offer_price: string;
  order_create: string;
  order_cancel: string;
  order_change: string;
  seat_availability: string;
  service_list: string;
}

interface providerType {
  id: string;
  name: string;
  link: string;
  capabilityIds: Array<keyof capabilitiesMap>
}

interface sectionType {
  id: string;
  title: string;
  description: string;
  providers: Array<providerType>;
}

interface filterType {
  buttonLabel: string;
  buttonAriaLabel: string;
  applyLabel: string;
  clearLabel: string;
  closeLabel: string;
  titleLabel: string;
  resultsStatus_one: string;
  resultsStatus_other: string;
  resetStatus: string;
  pillRemovedStatus: string;
}

interface noResultsType {
  message: string;
}

interface NoResultsProps {
  message: string;
  description?: string;
  icon?: React.ReactNode;
  id?: string;
  className?: string;
}

interface tableColumnType {
  key: string;
  label: string
}

interface sideBannerType {
  travelAgencyPerks: {
    title: string;
    items: Array<string>;
  };
  quickLinks: {
    title: string;
    links: Array<{ label: string, path: string }>;
  };
}

interface jsonDataType {
  sections: Array<sectionType>
  tableColumns: Array<tableColumnType>;
  capabilities: capabilitiesMap;
  filter: filterType;
  noResults: noResultsType;
  sideBanner: sideBannerType;
}

interface optionType {
  key: OptionKey;
  label: string;
}

interface CommonFilterProps {

  sectionId: string;
  filter: filterType;
  options: Array<optionType>;

  // Popover open/anchor state
  open: boolean;
  anchorEl: HTMLElement | null;
  triggerRef: React.RefObject<HTMLButtonElement>;

  // Selection state (draft = live edits in popover)
  draft: Array<OptionKey>;

  // Derived flags
  appliedCount: number;
  draftCount: number;
  isAllApply: boolean;
  isAllDraft: boolean;
  isApplyDisabled: boolean;
  isResetDisabled: boolean;

  // Handlers
  onToggle: (key: OptionKey) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: (opts?: { refocusTrigger?: boolean }) => void;
  onOpen: (e: React.MouseEvent<HTMLButtonElement>) => void;

  // Reports the filter panel's live bounding rect (position + size) while
  // open, so the parent Accordion can detect whether the panel overflows
  // past the accordion's own bottom edge and grow itself to contain it.
  onPanelRectChange?: (rect: DOMRect | null) => void;
}

interface FilterOptionItemProps {
  sectionId: string;
  option: optionType;
  index: number;
  total: number;
  checked: boolean;
  onToggle: (key: OptionKey) => void;
}

interface FilterPillsProps {
  pills: Array<optionType>;
  onRemove: (key: CapabilityKey) => void;
  sectionId: string;
}

interface CapabilityTagProps {
  label: string;
  id?: string;
  className?: string;
}

interface FilterPillButtonProps {
  label: string;
  onRemove: () => void;
  removeAriaLabel?: string;
  id?: string;
}

interface FilterPillItemProps {
  pill: optionType;
  onRemove: (key: CapabilityKey) => void;
  id: string;
}

type CapabilityKey = keyof capabilitiesMap
const ALL = 'all' as const
type OptionKey = typeof ALL | CapabilityKey

const useCapabilityFilter = (providers: Array<providerType>, capabilities: capabilitiesMap, allLabel: string) => {

  const capabilityKeys = useMemo(
    () => Object.keys(capabilities) as CapabilityKey[],
    [capabilities]
  )
  const options = useMemo(
    () => [{ key: ALL, label: allLabel },
    ...capabilityKeys.map((key) => ({ key: key, label: capabilities[key] }
    ))],
    [capabilityKeys, capabilities, allLabel]
  )

  const [draft, setDraft] = useState<Array<OptionKey>>([ALL])
  const [applied, setApplied] = useState<Array<OptionKey>>([ALL])

  const [open, setOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  // Flips true the first time the user actually applies a filter (Apply or
  // pill removal). Gates the results live region so it stays silent on
  // mount/expand and only announces after a real filter action.
  const [hasApplied, setHasApplied] = useState(false)

  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const toggle = useCallback((key: OptionKey) => {
    setDraft((prev) => {
      if (key === ALL) return [ALL];
      const next = prev.includes(key) ? prev.filter((k) => k !== key && k !== ALL) : [...prev.filter((k) => k !== ALL), key]
      return next.length === 0 ? [ALL] : next
    })
  }, [])

  const handleApply = useCallback(() => {
    setApplied(draft)
    setHasApplied(true)
    setOpen(false)
    triggerRef.current?.focus()
  }, [draft])

  // Reset only ticks the "All" option in the draft (popover) selection; the
  // actual table filtering is deferred until Apply is pressed, matching the
  // draft/applied model used everywhere else. `applied` is intentionally left
  // untouched here.
  const handleReset = useCallback(() => {
    setDraft([ALL]);
  }, []);

  const handleClose = useCallback((opts?: { refocusTrigger?: boolean }) => {
    setDraft(applied);              // discard un-applied edits
    setOpen(false);
    if (opts?.refocusTrigger ?? true) {
      triggerRef.current?.focus();
    }
  }, [applied]);

  // Hard close: also drop the (possibly stale) anchor. Used when the parent
  // Accordion collapses and the trigger button is about to unmount.
  const forceClose = useCallback(() => {
    setDraft(applied);
    setOpen(false);
    setAnchorEl(null);
  }, [applied]);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setDraft(applied);
    setOpen(true);
  }, [applied]);

  // Removing a single pill: drop that capability from both the applied
  // (live) selection and the draft (popover) selection, so the checkbox
  // reflects the change immediately even if the popover is/was open.
  // Falls back to [ALL] when nothing is left selected.
  const handleRemoveApplied = useCallback((key: CapabilityKey) => {
    setHasApplied(true);
    setApplied((prev) => {
      const next = prev.filter((k) => k !== key);
      return next.length === 0 ? [ALL] : next;
    });
    setDraft((prev) => {
      const next = prev.filter((k) => k !== key);
      return next.length === 0 ? [ALL] : next;
    });
  }, []);

  const providerSets = useMemo(
    () => providers.map((p) => ({
      provider: p,
      caps: new Set(p.capabilityIds),
    })),
    [providers]
  );

  const filteredProviders = useMemo(() => {
    if (applied.includes(ALL)) return providers;
    const sel = applied as CapabilityKey[];
    return providerSets
      .filter(({ caps }) => sel.some((cap) => caps.has(cap)))
      .map(({ provider }) => provider);
  }, [providers, providerSets, applied]);

  const appliedCount = applied.includes(ALL) ? 0 : applied.length;
  const draftCount = draft.includes(ALL) ? 0 : draft.length;

  // Pills shown next to the filter button — one per applied (non-ALL)
  // capability, so users can remove a single filter without opening the
  // popover.
  const appliedPills = useMemo(
    () => options.filter((option) => option.key !== ALL && applied.includes(option.key)),
    [options, applied]
  );

  return {
    options, draft, open, anchorEl, triggerRef, hasApplied,
    toggle, handleApply, handleReset, handleClose, handleOpen, forceClose,
    filteredProviders, appliedCount, draftCount, appliedPills,
    onRemoveApplied: handleRemoveApplied,
    isAllApplied: applied[0] === ALL,
    isAllDraft: draft[0] === ALL,
    // Apply is meaningless if nothing changed; Reset meaningless if already All
    isApplyDisabled: sameSelection(draft, applied),
    isResetDisabled: draft.length === 1 && draft[0] === ALL,
  };
}

function sameSelection(a: Array<OptionKey>, b: Array<OptionKey>) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((k) => set.has(k));
}

const getImgSrc = (value: string) => `/images/travelpartners/${value.toLowerCase().replace(/\s/g, '')}.png`

// Static sx objects hoisted to module scope so their identity is stable
// across renders — avoids recreating style objects (and the MUI sx
// processing cost that comes with them) on every render of memoized
// components that receive them.
const filterPopperSx = { zIndex: 2, minWidth: { xs: '311px', sm: 'auto', md: 'auto' } } as const;
const filterPopperModifiers: Array<{ name: string; enabled: boolean; options?: { mainAxis?: boolean; altAxis?: boolean; tether?: boolean } }> = [
  { name: 'flip', enabled: false },
  { name: 'preventOverflow', enabled: false },
];
const filterPanelSx = {
  mt: '10px',
  minWidth: { md: '229px', sm: '229px', xs: '100%' },
  boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  padding: '16px',
  '& :focus-visible': { outline: '2px solid black', outlineOffset: '-2px' },
} as const;
const filterPanelHeaderSx = { display: 'flex', justifyContent: 'space-between', width: '100%' } as const;
const filterPanelTitleSx = { fontWeight: 700 } as const;
const filterCloseButtonSx = {
  fontSize: '32px',
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' },
  justifyContent: 'end !important',
} as const;
const filterOptionsListSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'flex-start',
  listStyle: 'none',
  mb: '8px',
  mt: '8px',
} as const;
const filterCheckboxSx = {
  p: '0',
  mr: '10px',
  minWidth: '24px',
  minHeight: '24px',
  color: '#1460AA',
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px', justifyContent: 'end !important', '&:focus-visible': { outline: '2px solid black' } },
} as const;
const filterActionsSx = { display: 'flex', flexDirection: { xs: 'column', md: 'row', sm: 'row' }, gap: '16px' } as const;
const filterApplyButtonSx = { width: '100% !important', maxWidth: 'none !important', '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' } } as const;
const filterResetButtonSx = { width: '100% !important', maxWidth: 'none !important', '&:disabled': { color: '#A3A4A6', backgroundColor: "#EFF0F2", border:"1px solid", borderColor: "#C9CACC" }, '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' } } as const;
const filterTriggerIconSx = { color: 'white', fontSize: '16px' } as const;
const filterTriggerLabelSx = { fontSize: '14px' } as const;
const optionPositionSx = visuallyHidden;

/**
 * FilterOptionItem
 *
 * One checkbox row in the filter popover. Extracted + memoized so toggling
 * one option only re-renders that option's row, not the whole list — the
 * `onChange` closure is created here (once per row, only when `checked` or
 * `onToggle` identity changes) instead of inline in a `.map()` inside the
 * parent, which would otherwise recreate every row's closure on every
 * render of `CommonFilter`.
 */
const FilterOptionItem = React.memo(({ sectionId, option, index, total, checked, onToggle }: FilterOptionItemProps) => {
  const handleChange = useCallback(() => onToggle(option.key), [onToggle, option.key]);
  // Native checkboxes only toggle on Space; Enter normally does nothing (or
  // submits a form). Add Enter as an equivalent activation key for a11y,
  // while leaving Space/Tab to their default native behavior untouched.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onToggle(option.key);
    }
  }, [onToggle, option.key]);
  const checkboxId = `${sectionId}-checkbox-${index}`;
  const labelId = `${sectionId}-label-${index}`;
  const positionId = `${sectionId}-option-position-${index}`;

  return (
    <ListItem className="technology-partners-filter-option-item" disablePadding>
      <Checkbox
        id={checkboxId}
        className="technology-partners-filter-option-checkbox"
        checked={checked}
        sx={filterCheckboxSx}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputProps={{ 'aria-labelledby': labelId }}
      // no tabIndex override — real <input>, natural DOM-order tab stop
      />
      <label aria-hidden={true} id={labelId} htmlFor={checkboxId}><Box component="span" id={positionId} sx={optionPositionSx}>{`${index + 1} of ${total}`}</Box> {option.label}</label>
    </ListItem>
  );
});

const CommonFilter = React.memo(({
  sectionId,
  filter,
  options,
  open,
  anchorEl,
  triggerRef,
  draft,
  appliedCount,
  draftCount,
  isAllDraft,
  isAllApply,
  isApplyDisabled,
  isResetDisabled,
  onToggle,
  onApply,
  onReset,
  onClose,
  onOpen,
  onPanelRectChange,
}: CommonFilterProps) => {

  const closeRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = `${sectionId}-filter-title`;
  const dialogId = `${sectionId}-filter-dialog`;

  // Text announced by the dialog's polite live region. Empty by default so
  // nothing is read on open; set to the "filters reset" copy when Reset is
  // pressed, and cleared again whenever the dialog closes so a stale message
  // isn't re-announced on the next open.
  const [statusMessage, setStatusMessage] = useState('');

  // Measure the filter panel's live bounding rect while it's open, so the
  // parent Accordion can tell whether the panel's bottom edge overflows
  // past the accordion's own bottom edge (the Popper renders through a
  // portal, so it isn't clipped/contained by the accordion's own overflow).
  // ResizeObserver alone only catches size changes; scroll/resize listeners
  // catch position changes (e.g. page scroll moving the anchored popper).
  useEffect(() => {
    if (!open) {
      onPanelRectChange?.(null);
      return;
    }
    const el = panelRef.current;
    if (!el) return;

    const measure = () => onPanelRectChange?.(el.getBoundingClientRect());

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      onPanelRectChange?.(null);
    };
  }, [open, onPanelRectChange]);

  useEffect(() => {
    if (!open) return;
    // Defer focus with a small timeout (NOT rAF). rAF fires on the next paint
    // (~16ms), which moves focus into the popover so fast that NVDA drops the
    // trigger's "expanded" state announcement in favor of the newly focused
    // element. A short timeout lets the screen reader finish announcing
    // "expanded" before focus lands on the close button.
    // preventScroll avoids the page jumping to bring the element into view.
    const id = setTimeout(() => closeRef.current?.focus({ preventScroll: true }), 150);
    return () => clearTimeout(id);
  }, [open]);

  // Clear any lingering status message once the dialog closes so it isn't
  // re-announced the next time the dialog opens.
  useEffect(() => {
    if (!open) setStatusMessage('');
  }, [open]);

  // Stable handlers so children (backdrop, close button, Paper's onKeyDown)
  // don't get a new function identity on every render — matters here since
  // it avoids unnecessary prop churn feeding into memoized subtrees.
  const handleCloseClick = useCallback(() => onClose(), [onClose]);
  const handlePanelKeyDown = useCallback(() => {
    // No Escape handling: panel closes only from the explicit close button.
    // No Tab handling here — native order does the work.
  }, []);

  // Reset click ordering matters for screen readers. We want the "Filters
  // reset" status announced FIRST, then focus to advance — without the Reset
  // button announcing itself as "unavailable" when it becomes disabled.
  //
  // 1. Clear the live region to empty, then set the message on the next tick
  //    so the text content genuinely changes on every click (setting the same
  //    string again is a DOM no-op that screen readers won't re-announce).
  //    The reset itself is deferred, so the button stays enabled/focused while
  //    the polite status is announced.
  // 2. After a short delay (long enough for the status to be spoken), move
  //    focus to the close button FIRST, then call onReset(). Moving focus off
  //    the Reset button before it disables avoids NVDA announcing it as
  //    "unavailable"/"dimmed".
  const handleResetClick = useCallback(() => {
    setStatusMessage('');
    setTimeout(() => setStatusMessage(filter.resetStatus), 50);
    setTimeout(() => {
      closeRef.current?.focus({ preventScroll: true });
      onReset();
    }, 150);
  }, [onReset, filter.resetStatus]);

  const triggerLabel = !appliedCount ? filter.buttonLabel : `${filter.buttonLabel} (${appliedCount})`;
  const triggerAriaLabel = !appliedCount ? filter.buttonAriaLabel : `${filter.buttonAriaLabel} (${appliedCount})`;

  const triggerSx = useMemo(() => ({
    width: { xs: '100%', md: 'auto !important' },
    borderRadius: '999px',
    p: '4px 12px',
    whiteSpace: 'nowrap',
    maxWidth: { xs: 'none !important', sm: 'fit-content', md: 'fit-content' },
    backgroundColor: !open ? '#1460AA' : '#002345',
    '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' },
  }), [open]);
  const arrowSx = useMemo(() => ({ color: 'white', rotate: open ? '180deg' : '0deg' }), [open]);
  const applyAriaLabel = draftCount ? `${filter.applyLabel} (${draftCount})` : filter.applyLabel;

  return (
    <Box sx={{ width: { xs: '100%', md: 'auto', sm: 'auto' } }} className="technology-partners-filter">
      <Button
        id={`${sectionId}-filter-trigger`}
        className="technology-partners-filter-trigger"
        ref={triggerRef}
        onClick={onOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-label={triggerAriaLabel}
        variant='primary'
        size='xs'
        sx={triggerSx}
      >
        <Box sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'center', md: 'center' }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FilterListIcon aria-hidden='true' sx={filterTriggerIconSx} />
            <Typography component="span" sx={filterTriggerLabelSx}>{triggerLabel}</Typography>
          </Box>
          <ArrowDropDown aria-hidden='true' sx={arrowSx}></ArrowDropDown>
        </Box>

      </Button>

      <Popper className="technology-partners-filter-popper" open={open} anchorEl={anchorEl} sx={filterPopperSx} placement="bottom-end" disablePortal={true} modifiers={filterPopperModifiers}>
        <TrapFocus open={open} disableAutoFocus disableEnforceFocus>
          {/* tabIndex={-1} = not itself a Tab stop; TrapFocus needs a container node */}
          <Paper
            id={dialogId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="technology-partners-filter-panel"
            ref={panelRef}
            tabIndex={-1}
            sx={filterPanelSx}
            onKeyDown={handlePanelKeyDown}
          >
            <Box sx={filterPanelHeaderSx}>
              <Typography id={titleId} component="h3" sx={filterPanelTitleSx}>{filter.titleLabel}</Typography>
              <Button id={`${sectionId}-filter-close`} className="technology-partners-filter-close" ref={closeRef} aria-label={`${filter.closeLabel}`} variant='text' size='md' onClick={handleCloseClick} sx={filterCloseButtonSx} ><CloseIcon aria-hidden='true'></CloseIcon></Button>
            </Box>

            <List className="technology-partners-filter-options-list" sx={filterOptionsListSx}>
              {options.map((option, index) => (
                <FilterOptionItem
                  key={option.key}
                  sectionId={sectionId}
                  option={option}
                  index={index}
                  total={options.length}
                  checked={draft.includes(option.key)}
                  onToggle={onToggle}
                />
              ))}
            </List>


            <Box sx={filterActionsSx}>
              <Button id={`${sectionId}-filter-apply`} className="technology-partners-filter-apply" aria-label={applyAriaLabel} disabled={isApplyDisabled} variant='primary' size='xs' sx={filterApplyButtonSx} onClick={onApply}>{filter.applyLabel}</Button>
              <Button id={`${sectionId}-filter-reset`} className="technology-partners-filter-reset" disabled={isResetDisabled} variant='secondary' size='xs' sx={filterResetButtonSx} onClick={handleResetClick}>{filter.clearLabel}</Button>
            </Box>
            <Box id={`${sectionId}-filter-status`} className="technology-partners-filter-status" role="status" aria-live="polite" sx={optionPositionSx}>{statusMessage}</Box>
          </Paper>
        </TrapFocus>
      </Popper>
    </Box>
  )
})

/**
 * CapabilityTag
 *
 * Read-only capability tag used in the provider table cells. Deliberately a
 * plain, non-interactive <Box> — not a <Button> — since it has no action.
 * Rendering it as a real button would make it a dead, focusable control for
 * keyboard/screen-reader users (a real accessibility bug we removed).
 *
 * Kept separate from the filter pill (below) on purpose: the two no longer
 * share any styling (responsive/truncated tag vs. fixed-size filter chip),
 * so forcing them through one "sized" component would only add branching
 * cost to a node rendered many times per table for no reuse benefit.
 */
const capabilityTagSx = {
  display: 'flex',
  alignItems: 'center',
  bgcolor: '#F1F7FB',
  border: '1px solid',
  color: '#1460AA',
  borderColor: '#1460AA',
  borderRadius: '16px',
  whiteSpace: 'nowrap',
  fontWeight: 500,
  letterSpacing: '0px',
  maxWidth: '148px',
  maxHeight: '24px',
  p: '4px 16px',
  fontSize: '10px',
  lineHeight: '16px',
} as const;

const CapabilityTag = React.memo(({ label, id, className }: CapabilityTagProps) => (
  <Box id={id} className={className} sx={capabilityTagSx}>
    {label}
  </Box>
));

/**
 * FilterPillButton
 *
 * Removable "applied filter" chip shown next to the filter dropdown.
 * Fixed size, non-wrapping, no text decoration, identical across all
 * viewports (no breakpoints) per design spec — unlike the table's
 * CapabilityTag, this is always a real, focusable <Button> since it has an
 * action (removing the filter).
 */
const filterPillButtonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  bgcolor: '#DDECF5',
  border: '1px solid #1460AA',
  borderRadius: '999px',
  p: '4px 12px',
  minWidth: '126px',
  minHeight: '28px',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1460AA',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'none' },
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' },
} as const;
const filterPillCloseIconSx = { fontSize: '14px', color: '#1460AA' } as const;
const filterPillCloseButtonSx = {
  p: 0,
  minWidth: 'auto',
  '&:hover .MuiSvgIcon-root': { color: '#0D3F73' },
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' },
} as const;

const FilterPillButton = React.memo(({ label, onRemove, removeAriaLabel, id }: FilterPillButtonProps) => (
  <Box
    className="technology-partners-filter-pill"
    sx={filterPillButtonSx}
  >
    {label}
    <Button
      id={id}
      className="technology-partners-filter-pill-close"
      aria-label={removeAriaLabel ?? `Remove ${label}`}
      onClick={onRemove}
      sx={filterPillCloseButtonSx}
    >
      <CloseIcon aria-hidden='true' sx={filterPillCloseIconSx} />
    </Button>
  </Box>
));

const filterPillsListSx = { display: 'flex', flexWrap: 'wrap', gap: '8px', listStyle: 'none', p: 0, m: 0 } as const;
const filterPillListItemSx = { display: 'inline-flex' } as const;

/**
 * FilterPillItem
 *
 * One removable pill. Extracted + memoized (like FilterOptionItem) so the
 * `onRemove` closure is scoped to this pill's key and only recreated when
 * `onRemove` or the key itself changes, rather than every pill in the row
 * getting a brand-new inline arrow function on every render of FilterPills.
 */
const FilterPillItem = React.memo(({ pill, onRemove, id }: FilterPillItemProps) => {
  const handleRemove = useCallback(() => onRemove(pill.key as CapabilityKey), [onRemove, pill.key]);
  return (
    <Box component="li" sx={filterPillListItemSx}>
      <FilterPillButton
        id={id}
        label={pill.label}
        onRemove={handleRemove}
        removeAriaLabel={`Remove filter ${pill.label}`}
      />
    </Box>
  );
});

/**
 * FilterPills
 *
 * Renders one removable FilterPillButton per applied (non-"all") capability
 * filter. Clicking a pill's close icon removes just that capability — the
 * popover's checkbox for it becomes unchecked and the provider list
 * re-filters — without needing to reopen the filter dropdown.
 *
 * Kept dumb/presentational so it stays reusable and trivial to reason
 * about; all the actual filtering logic lives in useCapabilityFilter.
 */
const FilterPills = React.memo(({ pills, onRemove, sectionId }: FilterPillsProps) => {
  if (pills.length === 0) return null;

  return (
    <Box
      component="ul"
      className="technology-partners-filter-pills"
      sx={filterPillsListSx}
    >
      {pills.map((pill) => (
        <FilterPillItem key={pill.key} id={`${sectionId}-filter-pill-${pill.key}`} pill={pill} onRemove={onRemove} />
      ))}
    </Box>
  );
});

const tableContainerSx = { boxShadow: 'none', border: 'none' } as const;
const tableSx = {
  tableLayout: 'fixed',
  minWidth: { xs: '640px !important', sm: '640px', md: '728px' },
  borderLeft: '0px',
} as const;
const tableHeaderCellSx = { fontWeight: 700, backgroundColor: '#FFFFFF !important', fontSize: { xs: '14px', sm: '14px', md: '16px' }, lineHeight: '20px', letterSpacing: '0px' } as const;
const tableRowSx = { backgroundColor: '#FFFFFF !important' } as const;
const tableLogoCellSx = { maxWidth: { xs: '73px', sm: '73px', md: '200px' }, p: { xs: '2px', sm: '2px', md: '16px 24px' } } as const;
const tableLogoImgSx = { height: { xs: '11px', sm: '11px', md: '88px', objectFit: 'contain' }, width: { xs: '69px', sm: '69px', md: '152px' } } as const;
const providerLogoLinkSx = {
  display: 'inline-flex',
  '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid black', outlineOffset: '2px' },
} as const;
const tableCapabilitiesCellSx = { p: { xs: '8px', sm: '8px', md: '16px 24px' } } as const;
const tableCapabilitiesListSx = { display: 'flex', flexWrap: 'wrap', gap: '8px' } as const;
const tableColGroup = (
  <colgroup>
    <col style={{ width: '25%' }} />  {/* Provider/logo column - narrow */}
    <col style={{ width: '75%' }} />  {/* Capabilities column - wide */}
  </colgroup>
);

/**
 * ProviderRow
 *
 * One provider's table row. Extracted + memoized so that when the filtered
 * provider list changes, unaffected rows (same provider object reference)
 * skip re-rendering instead of the whole `<TableBody>` reconciling every
 * row's subtree from scratch.
 */
const ProviderRow = React.memo(({ provider, capabilities }: { provider: providerType; capabilities: capabilitiesMap }) => (
  <TableRow id={`technology-partners-row-${provider.id}`} className="technology-partners-row" sx={tableRowSx}>
    <TableCell className="technology-partners-logo-cell" align="center" sx={tableLogoCellSx}>
      <Box
        component="a"
        href={provider.link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={provider.name}
        id={`technology-partners-logo-link-${provider.id}`}
        className="technology-partners-logo-link"
        sx={providerLogoLinkSx}
      >
        <Box component="img" alt={provider.name} id={`technology-partners-logo-${provider.id}`} loading='lazy' decoding='async' className="technology-partners-logo" sx={tableLogoImgSx} src={getImgSrc(provider.id)} />
      </Box>
    </TableCell>
    <TableCell className="technology-partners-capabilities-cell" sx={tableCapabilitiesCellSx}>
      <Box className="technology-partners-capabilities-list" sx={tableCapabilitiesListSx}>
        {provider.capabilityIds.map((capabilityId) => (
          <CapabilityTag
            key={capabilityId}
            id={`technology-partners-capability-${provider.id}-${capabilityId}`}
            className="technology-partners-capability-chip"
            label={capabilities[capabilityId]}
          />
        ))}
      </Box>
    </TableCell>
  </TableRow>
));

const CommonTable = React.memo(({ tableColumns, providers, capabilities, tableLabel }: { tableColumns: Array<tableColumnType>; providers: Array<providerType>; capabilities: capabilitiesMap; tableLabel: string }) => {
  return (
    <TableContainer className="technology-partners-table-container" component={Paper} sx={tableContainerSx} tabIndex={0} role="region" aria-label={tableLabel}>
      <Table className="technology-partners-table" sx={tableSx} aria-label={tableLabel}>
        {tableColGroup}
        <TableHead className="technology-partners-table-head">
          <TableRow className="technology-partners-table-header-row">
            {tableColumns.map((column) => (
              <TableCell className={`technology-partners-table-header-cell technology-partners-column-${column.key}`} sx={tableHeaderCellSx} align='center' scope="col" key={column.key}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className="technology-partners-table-body">
          {providers.map((provider) => (
            <ProviderRow key={provider.id} provider={provider} capabilities={capabilities} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
});

/**
 * NoResults
 *
 * Small, presentational "empty state" screen shown when a filter selection
 * yields zero providers. Kept local to this file since it's only used here
 * and is simple enough not to warrant its own module.
 *
 * Accessibility: purely visual — the screen-reader announcement is handled
 * by the always-mounted polite live region in `CommonAccordian` (a live
 * region that mounts already-populated, as this component does, is
 * unreliably announced). The icon is decorative and hidden from assistive
 * tech.
 *
 * Responsive: guarantees a minimum height/width per breakpoint so the layout
 * never collapses to zero size on any viewport.
 *
 * Wrapped in React.memo since its props are primitives/stable nodes, making
 * the default shallow comparison cheap and effective.
 */
const noResultsRootSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  width: '100%',
  minHeight: { xs: '260px', sm: '280px', md: '380px' },
  minWidth: { xs: '100%', sm: '320px' },
  gap: { xs: '16px', md: '24px' },
  p: { xs: '24px 16px', sm: '32px 24px', md: '40px 32px' },
  boxSizing: 'border-box',
} as const;
const noResultsIconWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: { xs: '56px', md: '72px' },
  height: { xs: '56px', md: '72px' },
  borderRadius: '50%',
  backgroundColor: '#F1F7FB',
  flex: '0 0 auto',
} as const;
const noResultsIconSx = { color: '#1460AA', fontSize: { xs: '28px', md: '36px' } } as const;
const noResultsMessageSx = {
  fontWeight: 500,
  fontSize: { xs: '16px', sm: '16px', md: '16px' },
  lineHeight: { xs: '22px', sm: '24px', md: '28px' },
  color: '#002345',
  maxWidth: '480px',
} as const;
const noResultsDescriptionSx = {
  fontWeight: 400,
  fontSize: { xs: '14px', md: '16px' },
  lineHeight: { xs: '20px', md: '22px' },
  color: '#4A5967',
  maxWidth: '480px',
} as const;

const NoResults = React.memo(({ message, description, icon, id, className }: NoResultsProps) => {
  return (
    <Box
      id={id}
      className={className}
      sx={noResultsRootSx}
    >
      <Box aria-hidden="true" sx={noResultsIconWrapperSx}>
        {icon ?? <SearchIcon sx={noResultsIconSx} />}
      </Box>

      <Typography sx={noResultsMessageSx}>
        {message}
      </Typography>

      {description ? (
        <Typography sx={noResultsDescriptionSx}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
});

const accordionSx = {
  p: { xs: '16px', sm: '24px', md: '32px' },
  maxWidth: { xs: '100%', md: '792px' },
  maxHeight: { xs: '664px', sm: '614px', md: '892px' },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  borderRadius: '16px !important',
  boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)',
} as const;
const accordionExpandIconSx = { color: '#1460AA', fontSize: '24px' } as const;
const accordionSummarySx = {
  flex: '0 0 auto',
  p: 0,
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px', backgroundColor: 'transparent' },
  '& .MuiAccordionSummary-expandIconWrapper': {
    alignSelf: 'flex-start',
    mt: '14px',
  },
} as const;
const accordionSummaryContentSx = { display: 'flex', flexDirection: 'column', gap: { xs: '24px', md: '32px' } } as const;
const accordionTitleSx = {
  fontWeight: 700,
  fontSize: { xs: '16px', sm: '18px', md: '20px' },
  lineHeight: { xs: '22px', sm: '24px', md: '28px' },
  letterSpacing: '0px',
} as const;
const accordionDescriptionSx = {
  fontWeight: 400,
  fontSize: { xs: '14px', sm: '16px', md: '16px' },
  lineHeight: { xs: '20px', sm: '22px', md: '22px' },
  letterSpacing: '0px',
} as const;
const accordionDetailsSx = { flex: '1 1 auto', p: 0, mb:'16px',overflow: 'auto' } as const;
const accordionExpandIcon = <ExpandMoreIcon className="technology-partners-accordion-expand-icon" sx={accordionExpandIconSx} />;
const accordionFilterRowSx = {
  pt: '16px',
  pr: '5px',
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row', md: 'row' },
  gap: '16px',
  justifyContent: 'flex-end',
  alignItems: 'center',
} as const;

const CommonAccordian = React.memo(({ section, tableColumns, capabilities, filter, noResults, defaultExpanded }: { section: sectionType; tableColumns: Array<tableColumnType>; capabilities: capabilitiesMap, filter: filterType, noResults: noResultsType, defaultExpanded?: boolean }) => {

  const { t } = useTranslation();
  const fc = useCapabilityFilter(section.providers, capabilities, 'All')
  const [expanded, setExpanded] = useState(!!defaultExpanded)  // Pull out the stable (useCallback'd) forceClose so the dependency below is
  // a stable reference. Depending on the whole `fc` object instead would
  // rebuild this handler every render, since the hook returns a fresh object
  // literal each time.
  const { forceClose } = fc

  const accordionRef = useRef<HTMLDivElement>(null);

  // Locks in the accordion's height right after it finishes expanding (while
  // the filter is still unapplied/"All", i.e. the tallest the content will
  // ever be for this section) so that applying a filter afterwards — which
  // shrinks the table's row count — doesn't also visibly shrink the
  // accordion box itself. Reset to 0 on collapse so the next expand
  // re-measures fresh.
  const [minExpandedHeight, setMinExpandedHeight] = useState(0);

  const handleTransitionEntered = useCallback(() => {
    const el = accordionRef.current;
    if (!el) return;
    setMinExpandedHeight(el.getBoundingClientRect().height);
  }, []);

  // Per-instance slotProps (must be memoized, not the module-level constant,
  // since it now carries an onEntered callback bound to this accordion's ref).
  const accordionInstanceSlotProps = useMemo(() => ({
    transition: { unmountOnExit: true, onEntered: handleTransitionEntered },
  }), [handleTransitionEntered]);

  // Extra pixels to grow the accordion by so an expanded filter Popper
  // (rendered via a portal, so it isn't clipped/contained by the
  // accordion's own overflow) visually appears contained inside the
  // accordion instead of floating past its bottom edge.
  const [extraHeight, setExtraHeight] = useState(0);

  const handlePanelRectChange = useCallback((panelRect: DOMRect | null) => {
    if (!panelRect) {
      setExtraHeight(0);
      return;
    }
    const accordionEl = accordionRef.current;
    if (!accordionEl) return;

    const accordionRect = accordionEl.getBoundingClientRect();
    const overflowAmount = panelRect.bottom - accordionRect.bottom;

    setExtraHeight(overflowAmount > 0 ? overflowAmount + 8 : 0);
  }, []);

  // Merge the computed overflow delta and the locked-in minimum height into
  // the accordion's responsive maxHeight/minHeight so it only grows when the
  // open filter panel actually overflows past its bottom edge, and never
  // shrinks below its initial (unfiltered) expanded height; otherwise falls
  // back to the static sx object (stable reference, avoids unnecessary style
  // recalculation).
  const dynamicAccordionSx = useMemo(() => {
    if (!extraHeight && !minExpandedHeight) return accordionSx;
    return {
      ...accordionSx,
      ...(minExpandedHeight ? { minHeight: `${minExpandedHeight}px` } : {}),
      ...(extraHeight ? {
        maxHeight: {
          xs: `calc(664px + ${extraHeight}px)`,
          sm: `calc(614px + ${extraHeight}px)`,
          md: `calc(892px + ${extraHeight}px)`,
        },
      } : {}),
    };
  }, [extraHeight, minExpandedHeight]);

  const handleAccordionChange = useCallback((_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded)
    // When collapsing, hard-close the filter so its Popper/TrapFocus
    // unmounts while the anchor is still in the DOM (no stale float,
    // no lingering focus trap that blocks other accordions), and reset any
    // height grown/locked-in to contain a previously-open, overflowing
    // filter panel or an unfiltered provider list.
    if (!isExpanded) {
      forceClose()
      setExtraHeight(0)
      setMinExpandedHeight(0)
    }
  }, [forceClose])

  // Visually-hidden status text announced by the always-mounted polite live
  // region below. A live region that mounts already-populated (as the
  // conditionally-rendered `NoResults` would) is unreliably announced by
  // screen readers, so this persistent region carries both cases: the
  // no-results copy when the filter yields nothing, and a pluralized result
  // count otherwise. Memoized on the primitive count (plus the message/`t`)
  // so it only recomputes when the filtered total actually changes.
  const resultCount = fc.filteredProviders.length;
  const resultStatusMessage = useMemo(
    () => !fc.hasApplied
      ? ''
      : resultCount === 0
        ? noResults.message
        : t('ndc-program.connection-options.technologyPartners.filter.resultsStatus', { count: resultCount }),
    [fc.hasApplied, resultCount, noResults.message, t]
  );

  // Status announced when a single filter chip is removed. Removing a chip
  // may not change the row count (so the results region can stay silent), and
  // it also drops the removed chip's button from the DOM, so on its own NVDA
  // would go quiet and focus would fall back to <body>. This region announces
  // the new "N of M filters selected" state, and `handlePillRemove` below
  // moves focus to the next remaining chip.
  const [pillRemovedMessage, setPillRemovedMessage] = useState('');

  useEffect(() => {
    // Clear the pill-removed status when the accordion collapses so a stale
    // message isn't re-announced on the next expand.
    if (!expanded) setPillRemovedMessage('');
  }, [expanded]);

  const handlePillRemove = useCallback((key: CapabilityKey) => {
    // Snapshot the chip order BEFORE the removal so we can pick the neighbour
    // that should receive focus (the pill list re-renders synchronously once
    // state updates).
    const removedIndex = fc.appliedPills.findIndex((pill) => pill.key === key);
    const remainingPills = fc.appliedPills.filter((pill) => pill.key !== key);
    const totalBefore = fc.appliedPills.length;

    fc.onRemoveApplied(key);

    // Announce the new selection count. Cleared first then set on the next
    // tick so the live region text genuinely changes on every removal (setting
    // the same string is a DOM no-op that screen readers won't re-announce).
    setPillRemovedMessage('');
    setTimeout(
      () => setPillRemovedMessage(
        `${t('ndc-program.connection-options.technologyPartners.filter.pillRemovedStatus', {
          remaining: remainingPills.length,
          total: totalBefore,
        })}`
      ),
      50
    );

    // Move focus to the next logical chip: prefer the chip that shifts into
    // the removed one's position, else the new last chip, else fall back to
    // the filter trigger when no chips remain. Deferred so the DOM has
    // re-rendered (removed button gone, remaining IDs stable) before focusing.
    setTimeout(() => {
      const nextPill = remainingPills[removedIndex] ?? remainingPills[remainingPills.length - 1];
      if (nextPill) {
        document.getElementById(`${section.id}-filter-pill-${nextPill.key}`)?.focus({ preventScroll: true });
      } else {
        fc.triggerRef.current?.focus({ preventScroll: true });
      }
    }, 150);
  }, [fc, section.id, t]);

  return (
    <div id={`technology-partners-section-${section.id}`} className="technology-partners-section">
      <Accordion className="technology-partners-accordion"
        expanded={expanded}
        onChange={handleAccordionChange}
        slotProps={accordionInstanceSlotProps}
        ref={accordionRef}
        sx={dynamicAccordionSx}>
        <Box sx={accordionSummaryContentSx}>
          <AccordionSummary aria-describedby={`technology-partners-section-description-${section.id}`} className="technology-partners-accordion-summary" expandIcon={accordionExpandIcon} sx={accordionSummarySx}>
            <Typography component="h2" className="technology-partners-section-title" sx={accordionTitleSx}>
              {section.title}
            </Typography>
          </AccordionSummary>
          <Typography id={`technology-partners-section-description-${section.id}`} className="technology-partners-section-description" sx={accordionDescriptionSx}>
            {section.description}
          </Typography>
        </Box>
        <AccordionDetails className="technology-partners-accordion-details" sx={accordionDetailsSx}>
          <Box sx={accordionFilterRowSx}>
            <FilterPills pills={fc.appliedPills} onRemove={handlePillRemove} sectionId={section.id} />
            <CommonFilter
              sectionId={section.id}
              filter={filter}
              options={fc.options}
              open={fc.open}
              anchorEl={fc.anchorEl}
              triggerRef={fc.triggerRef}
              appliedCount={fc.appliedCount}
              draftCount={fc.draftCount}
              isApplyDisabled={fc.isApplyDisabled}
              isResetDisabled={fc.isResetDisabled}
              onToggle={fc.toggle}
              onApply={fc.handleApply}
              onReset={fc.handleReset}
              onClose={fc.handleClose}
              onOpen={fc.handleOpen}
              draft={fc.draft}
              isAllApply={fc.isAllApplied}
              isAllDraft={fc.isAllDraft}
              onPanelRectChange={handlePanelRectChange}
            ></CommonFilter>
          </Box>
          <Box
            id={`technology-partners-filter-pill-status-${section.id}`}
            className="technology-partners-filter-pill-status"
            role="status"
            aria-live="polite"
            sx={optionPositionSx}
          >
            {pillRemovedMessage}
          </Box>
          <Box
            id={`technology-partners-filter-status-${section.id}`}
            className="technology-partners-filter-status"
            role="status"
            aria-live="polite"
            sx={optionPositionSx}
          >
            {resultStatusMessage}
          </Box>
          {fc.filteredProviders.length === 0 ? (
            <NoResults
              id={`technology-partners-no-results-${section.id}`}
              className="technology-partners-no-results"
              message={noResults.message}
            />
          ) : (
            <CommonTable tableColumns={tableColumns} capabilities={capabilities} providers={fc.filteredProviders} tableLabel={section.title}></CommonTable>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  )
});

// Static sx for the top-level tab layout, side banner and its lists.
// Hoisted so TechnologyPartnersTab's re-renders (e.g. from live i18n
// context updates) don't recreate these objects and cascade prop changes
// into memoized children below.
const tabRootSx = { p: { xs: '32px 16px', sm: '40px 32px', md: '48px 120px' } } as const;
const tabContentSx = { display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'center', gap: '24px' } as const;
const tabSectionsListSx = { display: 'flex', flexDirection: 'column', gap: '24px' } as const;
const sideBannerSx = {
  maxHeight: { md: '450px' },
  width: { xs: '100%', md: '381px' },
  maxWidth: { xs: '100%', md: '792px' },
  backgroundColor: '#F1F7FB',
  p: { xs: '16px', sm: '24px', md: '32px' },
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
} as const;
const sideBannerSectionSx = { display: 'flex', flexDirection: 'column', gap: '24px' } as const;
const sideBannerSectionTitleSx = {
  fontWeight: 500,
  fontSize: { xs: '18px', sm: '20px', md: '20px' },
  lineHeight: { xs: '24px', sm: '28px', md: '32px' },
} as const;
const sideBannerPerksListSx = { display: 'flex', flexDirection: 'column', gap: '16px' } as const;
const sideBannerPerkItemSx = {
  fontWeight: 400,
  p: 0,
  fontSize: '16px',
  lineHeight: { xs: '20px', sm: '22px', md: '22px' },
  gap: '8px',
} as const;
const sideBannerPerkIconSx = { minWidth: 'auto !important', p: '0px' } as const;
const sideBannerPerkCheckSx = { color: '#20842F' } as const;
const sideBannerQuickLinksListSx = { display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'flex-start' } as const;
const sideBannerQuickLinkButtonSx = {
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: { xs: '20px', sm: '22px', md: '22px' },
  color: '#1460AA',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  textTransform: 'none',
  textWrap: 'nowrap',
  p: 0,
  '&.Mui-focusVisible': { outline: '2px solid black', outlineOffset: '2px' },
} as const;
const sideBannerQuickLinkArrowSx = { ml: '8px', fontSize: '16px', color: '#1460AA' } as const;

const TechnologyPartnersTab = () => {
  // const handleAnchorClick = useHandleAnchorClick();
  const { i18n, t } = useTranslation();
  const outerTheme: any = useTheme();
  // Inherits everything from the global/outer theme (palette, typography,
  // component overrides, etc.) and only overrides the `md` breakpoint so
  // other global styles don't break. Memoized so it isn't recreated on
  // every render — only when the outer theme actually changes.
  const technologyPartnersTheme = useMemo(
    () => createTheme({
      ...outerTheme,
      breakpoints: {
        values: {
          ...outerTheme.breakpoints.values,
          md: 1025,
        },
      },
    }),
    [outerTheme]
  );
  // Memoized so `jsonData`/its destructured members keep stable references
  // across renders where the translation namespace hasn't actually changed
  // — `t(..., { returnObjects: true })` otherwise returns a fresh object
  // every render, which would cascade new props into every CommonAccordian
  // (defeating their memoization) even when nothing changed.
  const jsonData: jsonDataType = useMemo(
    () => t('ndc-program.connection-options.technologyPartners', { returnObjects: true }),
    [t]
  );  
  const navigate = useNavigate()
  const handleNavigate = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    path!=='faq'?navigate(`/${i18n.language}/ndc-program/${path}`):navigate(`/${i18n.language}/support/${path}`);
  }
  const { sections, tableColumns, capabilities, filter, noResults, sideBanner } = jsonData
  return (
    <ThemeProvider theme={technologyPartnersTheme}>
      <Box id="technology-partners-tab" className="technology-partners-tab" sx={tabRootSx}>
        <Box className="technology-partners-content" sx={tabContentSx}>
          <Box className="technology-partners-sections-list" sx={tabSectionsListSx}>
            {sections.map((section, index) => (
              <CommonAccordian key={section.id} section={section} tableColumns={tableColumns} capabilities={capabilities} filter={filter} noResults={noResults} defaultExpanded={index === 0}></CommonAccordian>
            )
            )}
          </Box>
          <Box id="technology-partners-side-banner" className="technology-partners-side-banner" sx={sideBannerSx}>
            <Box className="technology-partners-side-banner-perks-section" sx={sideBannerSectionSx}>
              <Typography component="h3" className="technology-partners-side-banner-perks-title" sx={sideBannerSectionTitleSx}>
                {sideBanner.travelAgencyPerks.title}
              </Typography>
              <List className="technology-partners-side-banner-perks-list" sx={sideBannerPerksListSx} disablePadding>
                {sideBanner.travelAgencyPerks.items.map((item, index) => (
                  <ListItem key={index} id={`technology-partners-perk-item-${index}`} className="technology-partners-perk-item" sx={sideBannerPerkItemSx}>
                    <ListItemIcon className="technology-partners-perk-icon" sx={sideBannerPerkIconSx}>
                      <Check sx={sideBannerPerkCheckSx}></Check>
                    </ListItemIcon>
                    <Box component="span"><Box component="span" sx={optionPositionSx}>{`${index + 1} of ${sideBanner.travelAgencyPerks.items.length} `}</Box>{item}</Box>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Divider className="technology-partners-side-banner-divider" />
            <Box className="technology-partners-side-banner-quicklinks-section" sx={sideBannerSectionSx}>
              <Typography component="h3" className="technology-partners-side-banner-quicklinks-title" sx={sideBannerSectionTitleSx}>
                {sideBanner.quickLinks.title}
              </Typography>
              <Box className="technology-partners-side-banner-quicklinks-list" sx={sideBannerQuickLinksListSx}>
                {
                  sideBanner.quickLinks.links.map((item, index) => (
                    <Button role="link" key={index} id={`technology-partners-quicklink-${index}`} onClick={()=>handleNavigate(item.path)} className="technology-partners-quicklink-button" sx={sideBannerQuickLinkButtonSx}>
                      {item.label}
                      <KeyboardArrowRight sx={sideBannerQuickLinkArrowSx}></KeyboardArrowRight>
                    </Button>
                  ))
                }
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TechnologyPartnersTab;
